/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";

// Services
import {
  BeneficiaryService,
  GetBeneficiariesArgs,
} from "@/services/beneficiary.service";

/* -------------------------------------------------------------------------- */
/*                           Get Beneficiaries Hook                           */
/* -------------------------------------------------------------------------- */
const beneficiaryService = new BeneficiaryService();

type UseBeneficiariesOptions = {
  params?: GetBeneficiariesArgs;
};

const useBeneficiaries = ({ params }: UseBeneficiariesOptions = {}) => {
  const { data, isPending, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteQuery({
      queryFn: async ({ pageParam }) => {
        return await beneficiaryService.get({
          page: pageParam,
          pageSize: params?.pageSize,
          search: params?.search,
          status: params?.status,
          withVideo: params?.withVideo,
        });
      },
      queryKey: ["beneficiaries", params],
      placeholderData: keepPreviousData,
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          const nextPage = url.searchParams.get("page");
          return nextPage ? Number(nextPage) : undefined;
        }
        return undefined;
      },
    });

  return {
    data,
    isPending,
    fetchNextPage,
    hasNextPage: hasNextPage && data?.pages[data.pages.length - 1].next,
    isFetching,
  };
};

export { useBeneficiaries };
